// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles, productAvailabilityStyles } from "@bg/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@bg/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@bg/Overrides/Components/ProductPartNumbers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    // grid
    gridInformationWrapper?: InjectableCss;
    gridProductNameWrapper?: InjectableCss;
    gridProductNumbersWrapper?: InjectableCss;

    // list
    listInformationWrapper?: InjectableCss;
    listProductNameWrapper?: InjectableCss;
    listProductNumbersWrapper?: InjectableCss;
    listProductAvailabilityWrapper?: InjectableCss;

    // shared
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    listInformationWrapper: {
        css: css`
            padding-top: 11px;
            padding-bottom: 11px;
        `,
    },
    listProductNameWrapper: {
        css: css`
            min-height: 46px;
            margin-bottom: 5.5px;
        `,
    },
    listProductNumbersWrapper: {
        css: css`
            margin-bottom: 26px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 18,
                lineHeight: "22px",
            },
        },
        productDetailDescription: {
            size: 18,
            lineHeight: "22px",
        },
    },
    gridInformationWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.ui2")};
            border-bottom: 1px solid ${getColor("common.ui2")};
            padding: 11px 0 4px;
            margin-top: 4px;
        `,
    },
    gridProductNameWrapper: {
        css: css`
            min-height: 46px;
            margin-bottom: 19px;
        `,
    },
    gridProductNumbersWrapper: {
        css: css`
            min-height: 20px;
        `,
    },
    productAvailabilityStyles: {
        nonStockText: {
            css: css`
                ${productAvailabilityStyles.nonStockText?.css}
                max-width: 400px;
            `,
        },
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    view,
}: Props) => {
    const { product } = productContext;

    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            {view !== "List" && (
                <StyledWrapper {...styles.gridInformationWrapper} className="grid-information-wrapper">
                    {showTitle && (
                        <StyledWrapper {...styles.gridProductNameWrapper}>
                            <ProductDescription
                                lines={2}
                                lineHeight={20}
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        </StyledWrapper>
                    )}
                    {showPartNumbers && (
                        <StyledWrapper {...styles.gridProductNumbersWrapper}>
                            <ProductPartNumbers
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                            <StyledWrapper
                                css={css`
                                    min-height: 18px;
                                    display: flex;
                                `}
                            >
                                {product?.properties?.showIsDiscontinuedMessage?.toLowerCase() === "true" && (
                                    <Typography
                                        color="warning"
                                        size={fontConfig.fontSize.xSmall}
                                        lineHeight="17px"
                                        italic
                                    >
                                        {siteMessage("Custom_DiscontinuedProduct")}
                                    </Typography>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                    )}
                    <StyledWrapper>
                        <ProductSearchScoreData />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {view === "List" && (
                <StyledWrapper {...styles.listInformationWrapper} className="list-information-wrapper">
                    {showTitle && (
                        <StyledWrapper {...styles.listProductNameWrapper}>
                            <ProductDescription
                                lines={2}
                                lineHeight={20}
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        </StyledWrapper>
                    )}
                    {showPartNumbers && (
                        <StyledWrapper {...styles.listProductNumbersWrapper}>
                            <ProductPartNumbers
                                manufacturerItem={product.manufacturerItem}
                                productNumber={product.detail?.sku || product.productNumber}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                            <StyledWrapper>
                                {product?.properties?.showIsDiscontinuedMessage?.toLowerCase() === "true" && (
                                    <Typography
                                        color="warning"
                                        size={fontConfig.fontSize.xSmall}
                                        lineHeight="17px"
                                        italic
                                    >
                                        {siteMessage("Custom_DiscontinuedProduct")}
                                    </Typography>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                    )}
                    <StyledWrapper>
                        <ProductSearchScoreData />
                    </StyledWrapper>
                    {showAvailability && (
                        <StyledWrapper {...styles.listProductAvailabilityWrapper}>
                            <ProductContextAvailability
                                stockOnly={false}
                                extendedStyles={styles.productAvailabilityStyles as any}
                                {...({ isProductListPage: true } as any)}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
